import { Component, computed, inject, input, output, resource, signal } from '@angular/core';
import { FileResponse } from '../../../../core/clients/omni-core/models';
import {
  SimpleDialogLayout,
  Button,
  InputLabel,
  InputSimple,
  LoadingSpinner,
  ToggleLabel,
  Toggle,
  Skeleton,
  Select,
} from 'excellence-ui';
import { Api } from '../../../../core/clients/omni-core/api';
import { createFileShareToken } from '../../../../core/clients/omni-core/functions';
import { NgIcon } from '@ng-icons/core';
import { DatePipe } from '@angular/common';
import { TranslocoPipe } from '@jsverse/transloco';
import { form, required, FormField } from '@angular/forms/signals';
import { DEFAULT_EXPIRY_MINUTES } from './definitions';
import { ErrorComponent } from '../../../../shared/components/error-component/error-component';

@Component({
  selector: 'app-document-share-dialog',
  templateUrl: './document-share-dialog.html',
  imports: [
    SimpleDialogLayout,
    Button,
    NgIcon,
    DatePipe,
    InputLabel,
    InputSimple,
    LoadingSpinner,
    TranslocoPipe,
    ToggleLabel,
    Toggle,
    FormField,
    Skeleton,
    Select,
    ErrorComponent,
  ],
})
export class DocumentShareDialog {
  private api = inject(Api);

  document = input.required<FileResponse>();
  organizationID = input.required<string>();

  clickCancel = output();

  private model = signal({
    customExpiry: false,
    expiryMinutes: `${DEFAULT_EXPIRY_MINUTES}`,
  });
  protected form = form(this.model, (schemaPath) => {
    required(schemaPath.expiryMinutes);
  });
  private expiryMinutes = computed(() => {
    const expiry = this.model();
    return expiry.customExpiry ? parseInt(expiry.expiryMinutes, 10) : DEFAULT_EXPIRY_MINUTES;
  });

  protected copyState = signal<'idle' | 'loading' | 'success' | 'error'>('idle');

  protected shareLink = resource({
    params: () => ({
      oid: this.organizationID(),
      id: this.document().id,
      body: { expiryMinutes: this.expiryMinutes() },
    }),
    loader: ({ params }) =>
      this.api.invoke(createFileShareToken, params).then((res) => ({
        url: `${window.location.origin}/public/${res.token}`,
        expiresAt: res.expiresAt,
      })),
  });

  protected async copyLink(): Promise<void> {
    if (!this.shareLink.hasValue()) return;
    this.copyState.set('loading');
    try {
      await navigator.clipboard.writeText(this.shareLink.value().url);
      this.copyState.set('success');
    } catch {
      this.copyState.set('error');
    } finally {
      setTimeout(() => this.copyState.set('idle'), 2000);
    }
  }
}
